import { type CreateHookRunOptions, type HookRun } from '@lucidtech/las-sdk-browser';

import { create } from '@/hooks/api';
import { isHookRunId, JSONType } from '@/utils';

export type HookRunId = { hookId: string; runId: string };

export const {
  getOptions: getHookRunOptions,
  useBatchGet: useBatchGetHookRun,
  useCreate: useCreateHookRun,
  useDelete: useDeleteHookRun,
  useGet: useGetHookRun,
  useUpdate: useUpdateHookRun,
} = create<HookRun, HookRunId, CreateHookRunOptions & { hookId: string }>({
  cacheKey: 'hookRuns',
  equal: (hookRun, id) =>
    Boolean(hookRun && id?.hookId && id?.runId && hookRun.hookId === id.hookId && hookRun.runId === id.runId),
  createId: (hookRun) => (hookRun ? { hookId: hookRun.hookId, runId: hookRun.runId } : null),
  create: (client, { hookId, ...rest }) => client.createHookRun(hookId, { ...rest }),
  get: (client, id) => client.getHookRun(id.hookId, id.runId),
  delete: (client, id) => client.deleteHookRun(id.hookId, id.runId),
  list: (client, options) => {
    return new Promise((resolve, reject) => {
      client
        .listHookRuns(options)
        .then((response: JSONType) => {
          resolve({ data: response.runs, nextToken: response.nextToken });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  update: (client, id, updates) => client.updateHookRun(id.hookId, id.runId, updates),
});

export function toHookRunId(hookId?: string | null, runId?: string | null): HookRunId | null;
export function toHookRunId(runId?: string | null): HookRunId | null;

export function toHookRunId(hookIdOrRunId?: string | null, runId?: string | null): HookRunId | null {
  let _hookId = runId ? hookIdOrRunId : undefined;
  let _runId = runId ?? hookIdOrRunId;

  if (isHookRunId(_runId)) {
    [_hookId, _runId] = _runId.split('/');
  }

  return _hookId && _runId ? { hookId: _hookId, runId: _runId } : null;
}
